import React, { useState } from 'react';
import { CheckCircleIcon, ClockIcon, DownloadIcon, FileTextIcon, PenToolIcon, XCircleIcon } from 'lucide-react';
export function ESignatureCenter({
  vendor
}) {
  // State for the signature canvas
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({
    x: 0,
    y: 0
  });
  const [drawnPoints, setDrawnPoints] = useState([]);
  // State for selected document
  const [selectedDocument, setSelectedDocument] = useState(null);
  // State for showing the signature pad
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  // Mock documents that need signatures
  const [documents, setDocuments] = useState([{
    id: 1,
    name: 'Master Service Agreement',
    type: 'Contract',
    status: 'Pending Signature',
    requestedDate: '2023-10-20',
    dueDate: '2023-11-05',
    requestedBy: 'Acme Corp'
  }, {
    id: 2,
    name: 'Non-Disclosure Agreement',
    type: 'Legal',
    status: 'Signed',
    requestedDate: '2023-10-15',
    signedDate: '2023-10-16',
    requestedBy: 'Acme Corp'
  }, {
    id: 3,
    name: 'Statement of Work - Project Alpha',
    type: 'Contract',
    status: 'Pending Signature',
    requestedDate: '2023-10-22',
    dueDate: '2023-11-10',
    requestedBy: 'Acme Corp'
  }, {
    id: 4,
    name: 'Vendor Code of Conduct',
    type: 'Policy',
    status: 'Pending Signature',
    requestedDate: '2023-10-18',
    dueDate: '2023-11-01',
    requestedBy: 'Acme Corp'
  }, {
    id: 5,
    name: 'Data Processing Agreement',
    type: 'Legal',
    status: 'Signed',
    requestedDate: '2023-09-30',
    signedDate: '2023-10-05',
    requestedBy: 'Acme Corp'
  }]);
  // Format date for display
  const formatDate = dateString => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Calculate days remaining or overdue
  const getDaysRemaining = dateString => {
    const today = new Date();
    const dueDate = new Date(dateString);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Handle signature drawing
  const startDrawing = e => {
    const canvas = e.target;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setIsDrawing(true);
    setStartPoint({
      x,
      y
    });
    // Add the point to drawn points
    setDrawnPoints([...drawnPoints, {
      x,
      y
    }]);
    // Start drawing
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = e => {
    if (!isDrawing) return;
    const canvas = e.target;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Add the point to drawn points
    setDrawnPoints([...drawnPoints, {
      x,
      y
    }]);
    // Draw line
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDrawing = () => {
    setIsDrawing(false);
    // Capture the signature
    const canvas = document.getElementById('signature-canvas');
    if (canvas) {
      setSignature(canvas.toDataURL());
    }
  };
  const clearSignature = () => {
    const canvas = document.getElementById('signature-canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
      setDrawnPoints([]);
    }
  };
  // Handle document selection for signing
  const selectDocumentForSigning = document => {
    setSelectedDocument(document);
    setShowSignaturePad(true);
  };
  // Handle signature submission
  const submitSignature = () => {
    if (!signature || drawnPoints.length < 10) {
      alert('Please provide a valid signature');
      return;
    }
    // Update the document status
    const updatedDocuments = documents.map(doc => {
      if (doc.id === selectedDocument.id) {
        return {
          ...doc,
          status: 'Signed',
          signedDate: new Date().toISOString().split('T')[0]
        };
      }
      return doc;
    });
    setDocuments(updatedDocuments);
    setShowSignaturePad(false);
    setSelectedDocument(null);
    clearSignature();
    alert('Document signed successfully!');
  };
  // Cancel signing
  const cancelSigning = () => {
    setShowSignaturePad(false);
    setSelectedDocument(null);
    clearSignature();
  };
  // Filter documents by status
  const pendingDocuments = documents.filter(doc => doc.status === 'Pending Signature');
  const signedDocuments = documents.filter(doc => doc.status === 'Signed');
  return <div data-id="element-410">
      <div className="mb-8" data-id="element-411">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-412">eSignature Center</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-413">
          Sign documents and view signature history
        </p>
      </div>
      {/* Signature Modal */}
      {showSignaturePad && selectedDocument && <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center" data-id="element-414">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-id="element-415"></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 p-6" data-id="element-416">
            <div className="mb-4" data-id="element-417">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-418">
                Sign Document
              </h3>
              <p className="text-sm text-gray-500" data-id="element-419">{selectedDocument.name}</p>
            </div>
            <div className="mb-6" data-id="element-420">
              <p className="text-sm text-gray-700 mb-2" data-id="element-421">Please sign below:</p>
              <div className="border-2 border-gray-300 border-dashed rounded-md p-2 bg-gray-50" data-id="element-422">
                <canvas id="signature-canvas" width="500" height="200" className="w-full h-48 bg-white cursor-crosshair" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={endDrawing} onMouseLeave={endDrawing} data-id="element-423"></canvas>
              </div>
              <div className="mt-2 flex justify-end" data-id="element-424">
                <button type="button" onClick={clearSignature} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-425">
                  Clear Signature
                </button>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" data-id="element-426">
              <button type="button" onClick={submitSignature} disabled={!signature || drawnPoints.length < 10} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50" data-id="element-427">
                Sign Document
              </button>
              <button type="button" onClick={cancelSigning} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm" data-id="element-428">
                Cancel
              </button>
            </div>
          </div>
        </div>}
      {/* Pending Signatures */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-429">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-430">
          <div className="flex items-center" data-id="element-431">
            <PenToolIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-432" />
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-433">
              Documents Awaiting Signature
            </h3>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-434">
            {pendingDocuments.length} Pending
          </span>
        </div>
        <div className="border-t border-gray-200" data-id="element-435">
          {pendingDocuments.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-436">
              {pendingDocuments.map(document => {
            const daysRemaining = getDaysRemaining(document.dueDate);
            return <li key={document.id} className="px-4 py-4 sm:px-6" data-id="element-437">
                    <div className="flex items-center justify-between" data-id="element-438">
                      <div className="flex items-center" data-id="element-439">
                        <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-440" />
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-441">
                          {document.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex" data-id="element-442">
                        <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${daysRemaining <= 3 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-443">
                          {daysRemaining <= 0 ? 'Overdue' : 'Due Soon'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-444">
                      <div className="sm:flex" data-id="element-445">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-446">
                          {document.type}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-447">
                          Requested by: {document.requestedBy}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-448">
                        <div data-id="element-449">
                          <p data-id="element-450">Due: {formatDate(document.dueDate)}</p>
                          <p className={`text-xs mt-1 ${daysRemaining <= 0 ? 'text-red-600' : daysRemaining <= 3 ? 'text-red-600' : 'text-yellow-600'}`} data-id="element-451">
                            {daysRemaining <= 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days remaining`}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end" data-id="element-452">
                      <button type="button" onClick={() => selectDocumentForSigning(document)} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-453">
                        <PenToolIcon className="h-4 w-4 mr-1" data-id="element-454" />
                        Sign Now
                      </button>
                    </div>
                  </li>;
          })}
            </ul> : <div className="px-4 py-12 text-center" data-id="element-455">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" data-id="element-456" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-457">
                No pending signatures
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-458">
                You're all caught up! There are no documents awaiting your
                signature.
              </p>
            </div>}
        </div>
      </div>
      {/* Recently Signed */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-459">
        <div className="px-4 py-5 sm:px-6" data-id="element-460">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-461">
            Recently Signed Documents
          </h3>
        </div>
        <div className="border-t border-gray-200" data-id="element-462">
          <ul className="divide-y divide-gray-200" data-id="element-463">
            {signedDocuments.map(document => <li key={document.id} className="px-4 py-4 sm:px-6" data-id="element-464">
                <div className="flex items-center justify-between" data-id="element-465">
                  <div className="flex items-center" data-id="element-466">
                    <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-467" />
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-468">
                      {document.name}
                    </p>
                  </div>
                  <div className="ml-2 flex-shrink-0 flex" data-id="element-469">
                    <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-470">
                      Signed
                    </p>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between" data-id="element-471">
                  <div className="sm:flex" data-id="element-472">
                    <p className="flex items-center text-sm text-gray-500" data-id="element-473">
                      {document.type}
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-474">
                      Requested by: {document.requestedBy}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-475">
                    <p data-id="element-476">Signed: {formatDate(document.signedDate)}</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-end" data-id="element-477">
                  <button type="button" className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-478">
                    <DownloadIcon className="h-4 w-4 mr-1" data-id="element-479" />
                    Download
                  </button>
                </div>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}